import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface ParcelIssue {
  id: string
  issue_no: string
  agent_id: string | null
  agent_name?: string | null
  importer_name: string | null
  cyuk_job_ref: string | null
  waybill_number: string | null
  tracking_number: string | null
  cartons: number | null
  weight_kg: number | null
  declared_value: number | null
  currency: string
  carrier: string | null
  warehouse: string | null
  origin_country: string | null
  destination_country: string | null
  ship_date: string | null
  issue_type: 'lost' | 'damaged' | 'delayed' | 'missing_scan' | 'wrong_address' | null
  status: 'submitted' | 'investigating' | 'awaiting_carrier' | 'resolved' | 'closed'
  outcome: 'accepted' | 'rejected' | null
  issue_date: string | null
  investigation_started_at: string | null
  resolved_at: string | null
  payout_amount: number | null
  payout_year: number | null
  submitted_by: string | null
  assigned_to: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface ParcelIssueStats {
  total: number
  submitted: number
  investigating: number
  awaiting_carrier: number
  resolved: number
  closed: number
}

export function useParcelIssues(options?: {
  searchQuery?: string
  statusFilter?: string
  typeFilter?: string
  agentFilter?: string
}) {
  const [issues, setIssues] = useState<ParcelIssue[]>([])
  const [stats, setStats] = useState<ParcelIssueStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { searchQuery, statusFilter, typeFilter, agentFilter } = options || {}

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('parcel_issues')
        .select('*, customers(name)')
        .order('created_at', { ascending: false })

      if (searchQuery?.trim()) {
        const q = searchQuery.trim()
        query = query.or(
          `waybill_number.ilike.%${q}%,issue_no.ilike.%${q}%,importer_name.ilike.%${q}%,tracking_number.ilike.%${q}%,cyuk_job_ref.ilike.%${q}%`
        )
      }
      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }
      if (typeFilter && typeFilter !== 'all') {
        query = query.eq('issue_type', typeFilter)
      }
      if (agentFilter && agentFilter !== 'all') {
        query = query.eq('agent_id', agentFilter)
      }

      const { data, error: qErr } = await query
      if (qErr) throw qErr

      const rows = (data || []).map((r: any) => ({
        ...r,
        agent_name: r.customers?.name ?? null,
        customers: undefined,
      })) as ParcelIssue[]

      setIssues(rows)
      setStats({
        total: rows.length,
        submitted: rows.filter(r => r.status === 'submitted').length,
        investigating: rows.filter(r => r.status === 'investigating').length,
        awaiting_carrier: rows.filter(r => r.status === 'awaiting_carrier').length,
        resolved: rows.filter(r => r.status === 'resolved').length,
        closed: rows.filter(r => r.status === 'closed').length,
      })
    } catch (e: any) {
      setError(e.message || 'Failed to load parcel issues')
    } finally {
      setLoading(false)
    }
  }, [searchQuery, statusFilter, typeFilter, agentFilter])

  useEffect(() => { load() }, [load])

  const createIssue = useCallback(async (values: Partial<ParcelIssue>) => {
    const { data: noData, error: noErr } = await supabase.rpc('generate_parcel_issue_no')
    if (noErr) throw noErr
    const { data, error } = await supabase
      .from('parcel_issues')
      .insert([{ ...values, issue_no: noData }])
      .select()
      .single()
    if (error) throw error
    await load()
    return data as ParcelIssue
  }, [load])

  const updateIssue = useCallback(async (id: string, values: Partial<ParcelIssue>) => {
    const { data, error } = await supabase
      .from('parcel_issues')
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    await load()
    return data as ParcelIssue
  }, [load])

  return { issues, stats, loading, error, reload: load, createIssue, updateIssue }
}
